import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { PageMeta } from "@/components/site/PageMeta";
import { PageHero } from "@/components/site/PageHero";
import { Reveal } from "@/components/site/Reveal";
import { CTABanner } from "@/components/site/CTABanner";
import { PhotoBackground } from "@/components/site/PhotoBackground";
import { INSIGHTS } from "@/data/content";

const INSIGHTS_IMG =
    "https://images.unsplash.com/photo-1762572809147-3a00b76ae37e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHxkZXNlcnQlMjBkdXNrfGVufDB8fHx8MTc5MDk4ODc0Mnww&ixlib=rb-4.1.0&q=85";
const HERO_IMG =
    "https://images.unsplash.com/photo-1771270759486-1f7703945072?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaH3Y29ycG9yYXRlJTIwb2ZmaWNlfGVufDB8fHx8MTc5MDk4ODc0N3ww&ixlib=rb-4.1.0&q=85";
const LIST_IMG =
    "https://images.unsplash.com/photo-1707841460330-3e854714f057?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHxzYWd1YXJvJTIwZGVzZXJ0fGVufDB8fHx8MTc5MDk4ODc0Mnww&ixlib=rb-4.1.0&q=85";

const Meta = ({ article }) => (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
        <span className="text-blue-400">{article.category}</span>
        <span>{article.date}</span>
        <span>{article.readTime}</span>
    </div>
);

const Featured = ({ article }) => (
    <Reveal>
        <Link
            to={`/insights/${article.slug}`}
            data-testid={`insight-featured-${article.slug}`}
            className="group grid border border-amber-500/70 transition-colors duration-300 hover:border-amber-400 lg:grid-cols-2"
        >
            <div className="relative min-h-[18rem] overflow-hidden">
                <img
                    src={INSIGHTS_IMG}
                    alt="Desert ridgeline under a wide sky, editorial image for the featured briefing"
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#070a0f]/70 to-transparent" />
            </div>
            <div className="flex flex-col justify-between bg-[#0b0f16] p-8 lg:p-12">
                <div>
                    <Meta article={article} />
                    <h2 className="mt-6 font-serif text-3xl font-medium leading-tight tracking-tight text-slate-100 transition-colors duration-300 group-hover:text-white sm:text-4xl">
                        {article.title}
                    </h2>
                    <p className="mt-6 text-sm leading-relaxed text-slate-400 sm:text-base">
                        {article.excerpt}
                    </p>
                </div>
                <span className="mt-10 inline-flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.25em] text-slate-300">
                    Read the Briefing
                    <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
                </span>
            </div>
        </Link>
    </Reveal>
);

const ArticleRow = ({ article, index }) => (
    <Reveal delay={index * 0.06}>
        <Link
            to={`/insights/${article.slug}`}
            data-testid={`insight-card-${article.slug}`}
            className="group grid gap-4 border border-amber-500/70 bg-[#0b0f16]/60 px-5 py-10 transition-colors duration-300 hover:border-amber-400 hover:bg-[#0e141d] sm:grid-cols-12 sm:items-baseline sm:gap-8 sm:px-6"
        >
            <span className="font-mono text-[11px] tracking-[0.25em] text-amber-500 sm:col-span-1">
                0{index + 2}
            </span>
            <div className="sm:col-span-7">
                <Meta article={article} />
                <h3 className="mt-3 font-serif text-2xl font-medium leading-snug tracking-tight text-slate-100 transition-colors duration-300 group-hover:text-white sm:text-3xl">
                    {article.title}
                </h3>
            </div>
            <div className="flex items-start justify-between gap-6 sm:col-span-4">
                <p className="text-xs leading-relaxed text-slate-500">{article.excerpt}</p>
                <ArrowUpRight size={18} className="mt-1 shrink-0 text-slate-600 transition-all duration-300 group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:text-amber-400" />
            </div>
        </Link>
    </Reveal>
);

export default function Insights() {
    const featured = INSIGHTS.find((a) => a.featured);
    const rest = INSIGHTS.filter((a) => !a.featured);

    return (
        <>
            <PageMeta
                title="Insights | Nationwide Strategies LLC"
                description="Institutional analysis on regulatory compliance, complaint governance, operational performance, and leadership succession in the receivables industry."
            />
            <PageHero
                testid="insights-hero"
                kicker="Insights & Analysis"
                lines={["Briefings for the", "executive reader."]}
                lede="Occasional analysis on the questions collection industry leadership actually faces, written to be read in minutes and used for years."
                image={HERO_IMG}
            />
            <section className="relative overflow-hidden border-b border-slate-800/70">
                <PhotoBackground src={LIST_IMG} />
                <div className="relative mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-12 lg:py-24">
                    <Featured article={featured} />
                    <div className="mt-20 space-y-4">
                        {rest.map((a, i) => (
                            <ArticleRow key={a.slug} article={a} index={i} />
                        ))}
                    </div>
                </div>
            </section>
            <CTABanner />
        </>
    );
}
